/**
 * Тема интерфейса (D10): светлая и тёмная с переключателем.
 *
 * Три состояния, а не два: выбранная светлая, выбранная тёмная
 * и «не выбирал» — тогда действует системная. Поэтому в хранилище
 * лежит либо тема, либо ничего, а не «light» по умолчанию: иначе
 * первый заход в тёмной системе давал бы белую вспышку, и вернуть
 * «как в системе» после первого нажатия было бы уже нельзя.
 *
 * Значения цветов сюда не попадают: тема — это атрибут на корне
 * документа, а всё остальное делает `packages/brand/brand.css`.
 */

export const THEMES = ['light', 'dark'] as const

export type Theme = (typeof THEMES)[number]

export const THEME_STORAGE_KEY = 'rollcall.theme'

/** Хранилище выбора: то немногое от localStorage, что здесь нужно. */
export type ThemeStorage = Pick<Storage, 'getItem' | 'setItem'>

function isTheme(value: string | null): value is Theme {
  return value !== null && (THEMES as readonly string[]).includes(value)
}

/** Выбор человека, если он был сделан; `null` — «как в системе». */
export function readStoredTheme(storage: ThemeStorage): Theme | null {
  try {
    const stored = storage.getItem(THEME_STORAGE_KEY)
    return isTheme(stored) ? stored : null
  } catch {
    // Приватный режим Safari бросает на любом обращении к хранилищу.
    // Тема — не повод не открыть лист персонажа.
    return null
  }
}

export function storeTheme(storage: ThemeStorage, theme: Theme): void {
  try {
    storage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // Выбор не переживёт перезагрузку — это всё, что здесь ломается.
  }
}

/** Тема системы: `prefers-color-scheme`, если браузер о нём знает. */
export function systemTheme(view: Pick<Window, 'matchMedia'>): Theme {
  try {
    return view.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

/** С чего начинается сеанс: выбор человека, иначе тема системы. */
export function initialTheme(
  storage: ThemeStorage,
  view: Pick<Window, 'matchMedia'>,
): Theme {
  return readStoredTheme(storage) ?? systemTheme(view)
}

export function nextTheme(theme: Theme): Theme {
  return theme === 'dark' ? 'light' : 'dark'
}

/**
 * Применение темы: атрибут на корне документа, который читает
 * `brand.css`. Отдельная функция, потому что её зовёт и React,
 * и подготовительный скрипт до первой отрисовки.
 */
export function applyTheme(
  root: Pick<HTMLElement, 'setAttribute'>,
  theme: Theme,
): void {
  root.setAttribute('data-theme', theme)
}
